import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { ArrowRight } from '@phosphor-icons/react'
import { LoadingNeocoop } from '@/components/LoadingNeocoop'
import { BRAND, IS_DEMO } from '@/lib/brand'

export function LoginPage() {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [enteringApp, setEnteringApp] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    const { error } = await signIn(email, password)
    setSubmitting(false)
    if (error) {
      if (/invalid login credentials/i.test(error)) {
        setErr('Correo o contraseña no coinciden.')
      } else if (/email not confirmed/i.test(error)) {
        setErr('Falta confirmar el correo. Revisá tu bandeja.')
      } else {
        setErr(error)
      }
      return
    }
    // Splash post-login: muestra el isotipo brand 900ms antes de navegar al panel.
    // Da una transición premium en lugar de un cambio de pantalla abrupto.
    setEnteringApp(true)
    setTimeout(() => navigate('/', { replace: true }), 900)
  }

  // Splash post-login exitoso
  if (enteringApp) {
    return <LoadingNeocoop fullscreen label="Cargando NeoCore" />
  }

  const isValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && password.length >= 6

  return (
    <div className="login-root">
      {/* Decorative background — soft warm gradient + isotipo watermark */}
      <div className="login-bg" aria-hidden="true">
        <div className="login-bg-glow login-bg-glow-1" />
        <div className="login-bg-glow login-bg-glow-2" />
      </div>

      {IS_DEMO && (
        <img src={BRAND.logoSobreColor} alt={BRAND.nombre} className="demo-login-biglogo" />
      )}
      <form className="login-card" onSubmit={onSubmit}>
        {/* Hero strip — solo color brand, no hero saturado */}
        <div className="login-hero">
          <img
            src={BRAND.isoSobreColor}
            alt=""
            aria-hidden="true"
            className="login-hero-iso"
          />
        </div>

        <div className="login-content">
          <img
            src={BRAND.logo}
            alt={BRAND.nombre}
            className="login-logo"
          />

          <h1 className="login-title">Hola de nuevo</h1>
          <p className="login-sub">
            Iniciá sesión para continuar en <strong>NeoCore</strong>.
          </p>

          <div className="login-fields">
            <div className="login-field">
              <label className="login-field-label" htmlFor="email">
                Correo
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="vos@neocoop.gt"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (err) setErr(null)
                }}
              />
            </div>
            <div className="login-field">
              <label className="login-field-label" htmlFor="password">
                Contraseña
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  if (err) setErr(null)
                }}
              />
            </div>
          </div>

          {err ? (
            <div className="login-error">
              {err}
            </div>
          ) : null}

          <button
            type="submit"
            className="login-submit"
            disabled={!isValid || submitting}
          >
            {submitting ? 'Ingresando…' : (
              <>
                Ingresar <ArrowRight size={16} weight="bold" />
              </>
            )}
          </button>

          <p className="login-footer-line">
            {IS_DEMO ? "Demo · Datos ficticios · NeoCore" : "Equipo Neocoop · NeoCore v0.1"}
          </p>
        </div>
      </form>
    </div>
  )
}
